/**
 * Fila de escrituras por `Order`.
 *
 * Cadena de promesas por `orderId`: serializa las escrituras que tocan el
 * mismo documento `Order`. No es un candado global — dos actuaciones
 * distintas siguen yendo en paralelo. Solo se ponen en fila las que se
 * pisarían entre ellas (la subida de cada foto crea la `Image` y conecta
 * `Order.imageIDs` dentro de una transacción; dos a la vez sobre la misma
 * Order acaban en P2034 → `SAVE_TX_CONFLICT`).
 *
 * Vive en su propio módulo, sin Apollo ni almacenamiento, para poder
 * probarlo y para que la fase 2 del asistente de Actuación lo importe sin
 * arrastrar el sync manager entero.
 */
const chainByOrder = new Map<string, Promise<unknown>>();

export async function enSerieParaLaOrden<T>(
  orderId: string,
  trabajo: () => Promise<T>
): Promise<T> {
  const anterior = chainByOrder.get(orderId) ?? Promise.resolve();
  // `.catch` para que un fallo no rompa la cadena de los que vienen detrás:
  // cada uno reporta su propio error a su llamador.
  const actual = anterior.then(trabajo, trabajo);
  const eslabon = actual.catch(() => undefined);
  chainByOrder.set(orderId, eslabon);

  try {
    return await actual;
  } finally {
    // Liberar el Map cuando este era el último de la fila, para no acumular
    // una entrada por cada actuación que pase por aquí en toda la sesión.
    if (chainByOrder.get(orderId) === eslabon) {
      chainByOrder.delete(orderId);
    }
  }
}

/**
 * Retiene la fila de subidas de una Order mientras el llamador escribe ese
 * mismo documento, y devuelve la función que la suelta.
 *
 * Por qué existe: la fase 2 del asistente de Actuación encola las fotos
 * ANTES de su propio `saveAction` (el plan de fotos alimenta el cálculo de
 * `removeImages`), y encolar dispara el drenado inmediato del outbox
 * (`offline-image-batch-queued` → OfflineBanner). El `createImage` de cada
 * foto corría en paralelo con la transacción de `saveAction` sobre la misma
 * Order → `SAVE_TX_CONFLICT`, que la cola cuenta como reintento (tope 5):
 * con un guardado lento la foto acababa en `failed` y había que pulsar
 * Reintentar. Medido el 2026-09-04 con la básica del RV: 4 conflictos
 * seguidos con un `saveAction` de 4,9 s.
 *
 * Con la fila retenida, las subidas de esa Order esperan a que el llamador
 * suelte; las de otras Orders no se ven afectadas. Soltar es idempotente y
 * hay que hacerlo SIEMPRE (también en el `catch`): si no, la fila de esa
 * Order se queda parada hasta recargar la página.
 */
export function holdOrderUploads(orderId: string): () => void {
  let release: () => void = () => undefined;
  const gate = new Promise<void>((resolve) => {
    release = resolve;
  });
  void enSerieParaLaOrden(orderId, () => gate);
  return release;
}
